import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  ShoppingCart,
  Plus,
  Minus,
  Search,
  CreditCard,
  Smartphone,
  DollarSign,
  Trash2,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { api } from '@/services/api';

interface CartItem {
  id: string;
  productId: string;
  name: string;
  price: number;
  quantity: number;
  discount: number;
}

interface Product {
  id: string;
  sku: string;
  name: string;
  description?: string;
  price: number;
  stockQuantity: number;
  category: string;
  taxRate: number;
  isActive: boolean;
}

const POSPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [cart, setCart] = useState<CartItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'card' | 'mobile'>('cash');
  const [customerName, setCustomerName] = useState('');
  const [amountTendered, setAmountTendered] = useState('');

  const {
    data: products = [],
    isLoading: isLoadingProducts,
    isError: isProductsError,
  } = useQuery<Product[]>({
    queryKey: ['products'],
    queryFn: async () => {
      const res = await api.get('/api/products');
      return (res.data?.data?.products || res.data?.products || []) as Product[];
    },
  });

  const filteredProducts = products.filter((product: Product) =>
    product.isActive && product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const addToCart = (product: Product) => {
    setCart(prev => {
      const existingItem = prev.find(item => item.productId === product.id);
      if (existingItem) {
        return prev.map(item =>
          item.productId === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, {
        id: Date.now().toString(),
        productId: product.id,
        name: product.name,
        price: product.price,
        quantity: 1,
        discount: 0,
      }];
    });
  };

  const updateQuantity = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(itemId);
      return;
    }
    setCart(prev =>
      prev.map(item =>
        item.id === itemId ? { ...item, quantity } : item
      )
    );
  };

  const removeFromCart = (itemId: string) => {
    setCart(prev => prev.filter(item => item.id !== itemId));
  };

  const clearCart = () => {
    setCart([]);
    setCustomerName('');
    setAmountTendered('');
  };

  const calculateSubtotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const calculateDiscount = () => {
    return cart.reduce((sum, item) => sum + item.discount, 0);
  };

  const calculateVAT = () => {
    return (calculateSubtotal() - calculateDiscount()) * 0.165;
  };

  const calculateTotal = () => {
    return calculateSubtotal() - calculateDiscount() + calculateVAT();
  };

  const calculateChange = () => {
    const tendered = parseFloat(amountTendered) || 0;
    return Math.max(0, tendered - calculateTotal());
  };

  const createInvoiceMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        paymentMethod,
        items: cart.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
          unitPrice: item.price,
        })),
      };

      const res = await api.post('/api/sales', payload);
      return res.data?.data?.invoice || res.data?.invoice;
    },
    onSuccess: (invoice: { invoiceNumber?: string } | undefined) => {
      clearCart();
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['sales', 'invoices'] });
      if (invoice?.invoiceNumber) toast.success(`Sale completed: Invoice #${invoice.invoiceNumber}`);
      else toast.success('Sale completed successfully');
    },
    onError: (error: AxiosError<{ error?: string }>) => {
      const message = error.response?.data?.error || 'Sale failed. Please try again.';
      toast.error(message);
    },
  });

  const handleCheckout = async () => {
    if (cart.length === 0) return;
    await createInvoiceMutation.mutateAsync();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-MW', {
      style: 'currency',
      currency: 'MWK',
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Point of Sale
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Process sales transactions
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-600">
            Cart Items: {cart.length}
          </div>
          <Button variant="ghost" onClick={clearCart} disabled={cart.length === 0}>
            <Trash2 className="h-4 w-4 mr-2" />
            Clear Cart
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Products Section */}
        <div className="lg:col-span-2 space-y-4">
          {/* Search */}
          <Input
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={<Search className="h-4 w-4 text-gray-400" />}
          />

          {/* Products Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
            {isLoadingProducts && (
              <div className="col-span-2 sm:col-span-3 text-center text-sm text-gray-600 py-8">
                Loading products...
              </div>
            )}
            {isProductsError && (
              <div className="col-span-2 sm:col-span-3 text-center text-sm text-red-600 py-8">
                Failed to load products
              </div>
            )}
            {filteredProducts.map((product: Product, index: number) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="card p-4 cursor-pointer hover:shadow-medium transition-shadow duration-200"
                onClick={() => addToCart(product)}
              >
                <div className="text-center">
                  <div className="w-12 h-12 bg-gray-200 rounded-lg mx-auto mb-2"></div>
                  <h3 className="text-sm font-medium text-gray-900 mb-1">
                    {product.name}
                  </h3>
                  <p className="text-lg font-bold text-blue-600">
                    {formatCurrency(product.price)}
                  </p>
                  <p className="text-xs text-gray-500">
                    Stock: {product.stockQuantity}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Cart Section */}
        <div className="space-y-4">
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <ShoppingCart className="h-5 w-5 mr-2" />
                Cart
              </h3>
            </div>
            <div className="card-body">
              {/* Cart Items */}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {cart.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">
                    No items in cart
                  </p>
                ) : (
                  cart.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between py-2 border-b border-gray-100"
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {item.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatCurrency(item.price)} each
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="text-sm font-medium w-8 text-center">
                          {item.quantity}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFromCart(item.id)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Cart Summary */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium">{formatCurrency(calculateSubtotal())}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Discount</span>
                    <span className="font-medium">-{formatCurrency(calculateDiscount())}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">VAT (16%)</span>
                    <span className="font-medium">{formatCurrency(calculateVAT())}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200">
                    <span>Total</span>
                    <span className="text-blue-600">{formatCurrency(calculateTotal())}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Customer Info */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">
                Customer Information
              </h3>
            </div>
            <div className="card-body space-y-4">
              <Input
                label="Customer Name (Optional)"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Enter customer name"
              />
            </div>
          </div>

          {/* Payment Method */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">
                Payment Method
              </h3>
            </div>
            <div className="card-body space-y-3">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="payment"
                  value="cash"
                  checked={paymentMethod === 'cash'}
                  onChange={(e) => setPaymentMethod(e.target.value as 'cash' | 'card' | 'mobile')}
                  className="mr-2"
                />
                <span className="flex items-center">
                  <DollarSign className="h-4 w-4 mr-2" />
                  Cash
                </span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="payment"
                  value="card"
                  checked={paymentMethod === 'card'}
                  onChange={(e) => setPaymentMethod(e.target.value as 'cash' | 'card' | 'mobile')}
                  className="mr-2"
                />
                <span className="flex items-center">
                  <CreditCard className="h-4 w-4 mr-2" />
                  Card
                </span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="payment"
                  value="mobile"
                  checked={paymentMethod === 'mobile'}
                  onChange={(e) => setPaymentMethod(e.target.value as 'cash' | 'card' | 'mobile')}
                  className="mr-2"
                />
                <span className="flex items-center">
                  <Smartphone className="h-4 w-4 mr-2" />
                  Mobile Money
                </span>
              </label>
            </div>
          </div>

          {/* Amount Tendered */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">
                Payment
              </h3>
            </div>
            <div className="card-body space-y-4">
              <Input
                label="Amount Tendered"
                type="number"
                value={amountTendered}
                onChange={(e) => setAmountTendered(e.target.value)}
                placeholder="Enter amount received"
              />
              {parseFloat(amountTendered) > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Change</span>
                  <span className="font-medium text-green-600">
                    {formatCurrency(calculateChange())}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Checkout Button */}
          <Button
            onClick={handleCheckout}
            disabled={cart.length === 0 || !amountTendered || parseFloat(amountTendered) < calculateTotal() || createInvoiceMutation.isPending}
            loading={createInvoiceMutation.isPending}
            className="w-full"
          >
            {createInvoiceMutation.isPending ? 'Processing...' : 'Complete Sale'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default POSPage;
